import { useEffect } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags
    document.title = 'About Us | Evernorth AI | AI Consulting in Northwest Georgia';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about Evernorth AI, founded by Anthony Montelongo. We provide practical AI training and consulting for businesses in Northwest Georgia, helping teams adopt AI with confidence.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/about`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Evernorth AI",
      "description": "Learn about Evernorth AI and our mission to help businesses in Northwest Georgia adopt AI with confidence",
      "url": `${import.meta.env.VITE_SITE_URL}/about`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Evernorth AI",
        "founder": {
          "@type": "Person",
          "name": "Anthony Montelongo"
        },
        "description": "AI consulting and training services for businesses in Northwest Georgia",
        "areaServed": "Northwest Georgia",
        "knowsAbout": ["Artificial Intelligence", "AI Training", "Business Consulting", "Workforce Development"]
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  const values = [
    {
      icon: 'ri-user-heart-line',
      title: 'People First',
      description: 'Every business is built around helping people. We guide teams through AI transition without anyone getting left behind.'
    },
    {
      icon: 'ri-feedback-line',
      title: 'We Listen',
      description: 'We start by listening to understand your team, your work, and your goals before recommending any solutions.'
    },
    {
      icon: 'ri-book-open-line',
      title: 'Teach What\'s Useful',
      description: 'We teach only what is practical and applicable to your everyday work, not theoretical concepts.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Build What\'s Used',
      description: 'We build only what will actually be used by your team, ensuring steady improvement without disruption.'
    }
  ];

  return (
    <PageLayout>
      <PageHero
        badge="About Evernorth AI"
        title="Guiding Local Teams"
        titleHighlight="Through the AI Transition"
        description="Practical AI training and workflow support for Northwest Georgia organizations"
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-navy">
                About the
                <span className="text-gold"> Founder</span>
              </h2>
              <p className="text-lg text-charcoal mb-6 leading-relaxed">
                Evernorth AI was founded by Anthony Montelongo.
              </p>
              <p className="text-lg text-charcoal mb-6 leading-relaxed">
                Anthony has worked in both small family businesses and large corporations, giving him a grounded understanding of how different offices operate. He has 10+ years managing operations in hotels and resorts, local residential construction and recreational vehicles. He has been an entrepreneur in Northwest Georgia for over 5 years.
              </p>
              <p className="text-lg text-charcoal mb-6 leading-relaxed">
                Anthony builds every business around helping people. He understood that most people feel scared, nervous or confused by AI, which led him to start the first company in Northwest Georgia to guide people through this transition. Anthony deeply cares about the region and its success.
              </p>
              <p className="text-lg text-charcoal mb-8 leading-relaxed font-semibold">
                Evernorth AI was created to guide local teams through the AI transition without anyone getting left behind.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://calendly.com/evernorthai/discovery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-3 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Schedule Discovery Call
                </a>
                <Link
                  to="/services"
                  className="px-8 py-3 bg-navy/10 text-navy font-semibold rounded-full hover:bg-navy/20 transition-all border border-navy/20 cursor-pointer whitespace-nowrap text-center"
                >
                  Our Services
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="relative h-[600px] w-full rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/d306acce640bbe6e33338ad5a85786f4.jpg"
                  alt="Evernorth AI Team"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent"></div>
              </div>

              <div className="absolute -bottom-8 -left-8 bg-gradient-to-br from-gold to-[#a68a4e] p-8 rounded-2xl shadow-2xl max-w-xs">
                <div className="text-4xl font-bold text-white mb-2">10+</div>
                <div className="text-white/90 text-sm">Years Managing Operations Across Multiple Industries</div>
              </div>
            </div>
          </div>

          <div className="mt-32 mb-16 text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-navy">
              Our <span className="text-gold">Mission</span>
            </h2>
            <p className="text-lg text-charcoal leading-relaxed">
              Evernorth AI exists to help local organizations work smarter and feel confident in a changing world. We train teams, design workflows that make life easier, and give leaders a clearer path to better operations. We are your AI partner who can teach, build, and execute.
            </p>
          </div>

          <div className="mb-32 bg-gradient-to-br from-navy/5 to-gold/5 rounded-2xl p-12 border border-navy/10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-navy text-center">
              Our Vision for <span className="text-gold">Northwest Georgia</span>
            </h2>
            <p className="text-lg text-charcoal leading-relaxed text-center max-w-4xl mx-auto">
              Evernorth AI becomes the trusted standard for practical AI training and workflow design in Northwest Georgia. We want every organization in our region to feel prepared and confident as AI alters everyday work. Our long term goal is to guide local teams with steady leadership so they can stay competitive into the future.
            </p>
          </div>

          <div className="mb-16 text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-navy">
              How We Work <span className="text-gold">With Clients</span>
            </h2>
            <p className="text-lg text-charcoal leading-relaxed mb-4">
              We work at a pace that feels realistic for your team.
            </p>
            <p className="text-lg text-charcoal leading-relaxed">
              We start by listening. We teach only what is useful. We build only what will be used. Our goal is steady improvement, not disruption.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-[#f8f8f9] p-6 rounded-xl border border-soft-gray/30 hover:border-gold/50 transition-all"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-lg mb-4">
                  <i className={`${value.icon} text-2xl text-gold`}></i>
                </div>
                <h3 className="text-lg font-bold mb-2 text-navy">{value.title}</h3>
                <p className="text-sm text-charcoal leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-20 bg-gradient-to-br from-navy to-[#062299] p-12 rounded-3xl shadow-2xl text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Ready to Get Started?
            </h3>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Schedule a 15-minute discovery call to see if Foundations is right for your team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://calendly.com/evernorthai/discovery"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 sm:px-8 sm:py-4 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all shadow-lg hover:shadow-xl cursor-pointer text-sm sm:text-base text-center"
              >
                Schedule a Discovery Call
              </a>
              <a
                href="mailto:info@evernorthai.com"
                className="px-6 py-3 sm:px-8 sm:py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/20 transition-all border border-white/30 cursor-pointer text-sm sm:text-base text-center"
              >
                Not Ready To Talk? Email Us!
              </a>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
